import { STATION_RUN_RULES as R, stationRunAnswer, type StationRunSegment } from '../../../../shared/stationRunRules';
import type { FinishPlayAttemptInput, StationRunQuestionResult } from '../../../../shared/results';
import type { StationRunConfig } from './stationLevels';
import { TRACK, createSegment, laneX, type Obstacle, type SegmentLayout } from './trackLayout';

/** Koşu fiziği. Mesafe m, hız m/sn, süre ms. Dünya ileri doğru artan mesafeyle ölçülür. */
export const RUN = {
  stepMs: 5, countdownMs: 2400,
  baseSpeed: 10, speedPerSegment: 0.5, maxSpeed: 15, accel: 9,
  laneChangeMs: 170,
  /** Zıplama: 0,7 sn havada, 1,6 m tepe. Havada "kay" hızlı iniş yapar. */
  jumpHeight: 1.6, jumpMs: 700, fastDropSpeed: 22,
  slideMs: 750,
  /** İstasyona son bu kadar metrede yavaşlanır ve peronda durulur. */
  brakeDistance: 20,
  correctFeedbackMs: 1300, wrongFeedbackMs: 2300,
  turboFactor: 1.25, turboMs: 1500,
  /** Çarpma: kısa sendeleme, biraz geride güvenli bir şeritte yeniden doğma ve kalkan. */
  crashMs: 900, shieldMs: 2000, respawnBack: 10, respawnClearAhead: 25, respawnSpeedFactor: 0.5,
  playerHalfWidth: 0.35, playerHalfDepth: 0.35, standHeight: 1.8, slideHeight: 0.9,
} as const;

const GRAVITY = (8 * RUN.jumpHeight) / (RUN.jumpMs / 1000) ** 2;
const JUMP_VELOCITY = (4 * RUN.jumpHeight) / (RUN.jumpMs / 1000);

export type RunPhase = 'ready' | 'countdown' | 'running' | 'arriving' | 'question' | 'feedback' | 'crash' | 'finished' | 'lost';
/** `seq` artan sıra numarasıdır; sunum ve ses katmanı yeni olayları buna göre ayırır. */
export type RunEvent = { kind: 'coin' | 'hit' | 'jump' | 'slide' | 'lane' | 'station' | 'correct' | 'wrong' | 'respawn' | 'start'; at: number; seq: number };

export type RunState = {
  phase: RunPhase; clock: number; phaseStarted: number;
  /** Hareketli trenlerin saati: yalnız koşu sırasında ilerler (soru ve çarpmada donar). */
  runClock: number;
  distance: number; maxDistance: number; speed: number; arriveSpeed: number;
  segmentIndex: number; segments: SegmentLayout[]; segmentEvents: StationRunSegment[];
  lane: number; x: number; laneFrom: number; laneStarted: number;
  y: number; vy: number; slideStarted: number | null;
  score: number; lives: number; correct: number; wrong: number;
  turboUntil: number; shieldUntil: number; crashed: Obstacle | null;
  movingActivated: Record<string, number>;
  results: StationRunQuestionResult[]; questionOpenedAt: number; feedbackUntil: number;
  lastAnswer: { correct: boolean; choice: number; livesLost: 0 | 1; scoreDelta: number } | null;
  events: RunEvent[]; eventSeq: number; revision: number; accumulator: number;
  /** Öğretici göstergeler için: öğrenci ne kadar şerit değiştirdi / zıpladı / kaydı. */
  moves: { lane: number; jump: number; slide: number };
};

export function createRun(config: StationRunConfig): RunState {
  if (!config.questions.length) throw new Error('Soru bulunamadı.');
  return { phase: 'ready', clock: 0, phaseStarted: 0, runClock: 0, distance: 0, maxDistance: 0, speed: 0, arriveSpeed: 0,
    segmentIndex: 0, segments: config.questions.map((_, i) => createSegment(config.seed, i)), segmentEvents: [],
    lane: 1, x: 0, laneFrom: 0, laneStarted: 0, y: 0, vy: 0, slideStarted: null,
    score: 0, lives: R.startingLives, correct: 0, wrong: 0, turboUntil: 0, shieldUntil: 0, crashed: null, movingActivated: {},
    results: [], questionOpenedAt: 0, feedbackUntil: 0, lastAnswer: null, events: [], eventSeq: 0, revision: 0, accumulator: 0,
    moves: { lane: 0, jump: 0, slide: 0 } };
}

function setPhase(s: RunState, phase: RunPhase) { s.phase = phase; s.phaseStarted = s.clock; s.revision++; }
function event(s: RunState, kind: RunEvent['kind']) { s.eventSeq += 1; s.events.push({ kind, at: s.clock, seq: s.eventSeq }); if (s.events.length > 40) s.events.shift(); }
const stationAt = (index: number) => (index + 1) * R.segmentMeters;
export const segmentStart = (index: number) => index * R.segmentMeters;

export function startRun(s: RunState) { if (s.phase === 'ready') setPhase(s, 'countdown'); }

const CONTROLLABLE: readonly RunPhase[] = ['running', 'arriving'];
export const sliding = (s: RunState) => s.slideStarted !== null && s.clock - s.slideStarted < RUN.slideMs;

export function steer(s: RunState, direction: -1 | 1): boolean {
  const lane = s.lane + direction;
  if (!CONTROLLABLE.includes(s.phase) || lane < 0 || lane >= TRACK.lanes) return false;
  s.laneFrom = s.x; s.laneStarted = s.clock; s.lane = lane; s.moves.lane++; event(s, 'lane'); s.revision++;
  return true;
}

export function jump(s: RunState): boolean {
  if (s.phase !== 'running' || s.y > 0.001) return false;
  s.vy = JUMP_VELOCITY; s.slideStarted = null; s.moves.jump++; event(s, 'jump'); s.revision++;
  return true;
}

export function slide(s: RunState): boolean {
  if (s.phase !== 'running') return false;
  if (s.y > 0.001) s.vy = Math.min(s.vy, -RUN.fastDropSpeed);
  s.slideStarted = s.clock; s.moves.slide++; event(s, 'slide'); s.revision++;
  return true;
}

/** Hareketli tren oyuncuya yaklaşırken anlık konumu; diğer engeller sabittir. */
export function obstacleSpan(s: RunState, o: Obstacle): { start: number; end: number } {
  if (o.kind !== 'moving-train') return o;
  const activated = s.movingActivated[o.id];
  const moved = activated === undefined ? 0 : TRACK.movingTrainSpeed * (s.runClock - activated) / 1000;
  return { start: o.start - moved, end: o.end - moved };
}

function obstacleHeight(o: Obstacle): [number, number] {
  if (o.kind === 'barrier') return [0, TRACK.barrierHeight];
  if (o.kind === 'sign') return [TRACK.signBottom, TRACK.signTop];
  return [0, TRACK.trainHeight];
}

function hitTest(s: RunState): Obstacle | null {
  const height = sliding(s) ? RUN.slideHeight : RUN.standHeight;
  for (const segment of s.segments.slice(Math.max(0, s.segmentIndex - 1), s.segmentIndex + 2)) {
    for (const o of segment.obstacles) {
      if (o.cleared) continue;
      const span = obstacleSpan(s, o);
      if (span.end < s.distance - RUN.playerHalfDepth || span.start > s.distance + RUN.playerHalfDepth) continue;
      if (Math.abs(laneX(o.lane) - s.x) > TRACK.obstacleHalfWidth + RUN.playerHalfWidth) continue;
      const [bottom, top] = obstacleHeight(o);
      if (s.y < top && s.y + height > bottom) return o;
    }
  }
  return null;
}

function collectCoins(s: RunState) {
  const height = sliding(s) ? RUN.slideHeight : RUN.standHeight;
  const segment = s.segments[s.segmentIndex];
  for (const coin of segment.coins) {
    if (coin.taken || Math.abs(coin.at - s.distance) > 0.8 || Math.abs(laneX(coin.lane) - s.x) > 1) continue;
    if (coin.y < s.y - 0.3 || coin.y > s.y + height + 0.3) continue;
    coin.taken = true; s.score += R.coinScore; s.segmentEvents[s.segmentIndex].coins++; event(s, 'coin'); s.revision++;
  }
}

function laneClear(s: RunState, lane: number, from: number, to: number) {
  return s.segments.every((segment) => segment.obstacles.every((o) => {
    if (o.cleared || o.lane !== lane) return true;
    const span = obstacleSpan(s, o);
    return span.end < from || span.start > to;
  }));
}

/** Biraz geride, önündeki yol açık bir şeritte yeniden doğar; önce mevcut şerit, sonra yakın şeritler denenir. */
function respawn(s: RunState) {
  const hit = s.crashed!;
  const from = Math.max(segmentStart(s.segmentIndex) + 1, obstacleSpan(s, hit).start - RUN.respawnBack);
  s.distance = Math.min(s.distance, from);
  const order = [s.lane, s.lane - 1, s.lane + 1, s.lane - 2, s.lane + 2].filter((lane) => lane >= 0 && lane < TRACK.lanes);
  const lane = order.find((candidate) => laneClear(s, candidate, s.distance - 1, s.distance + RUN.respawnClearAhead)) ?? s.lane;
  s.lane = lane; s.x = laneX(lane); s.laneFrom = s.x; s.laneStarted = s.clock; s.y = 0; s.vy = 0; s.slideStarted = null;
  s.speed = targetSpeed(s) * RUN.respawnSpeedFactor; s.shieldUntil = s.clock + RUN.shieldMs; s.crashed = null;
  event(s, 'respawn'); setPhase(s, 'running');
}

function targetSpeed(s: RunState) {
  const base = Math.min(RUN.maxSpeed, RUN.baseSpeed + RUN.speedPerSegment * s.segmentIndex);
  return base * (s.clock < s.turboUntil ? RUN.turboFactor : 1);
}

function beginSegment(s: RunState) {
  s.segmentEvents.push({ coins: 0, hits: 0 });
  setPhase(s, 'running');
}

function step(s: RunState, config: StationRunConfig, dtMs: number) {
  const dt = dtMs / 1000;
  s.clock += dtMs;
  if (s.phase === 'countdown') { if (s.clock - s.phaseStarted >= RUN.countdownMs) { beginSegment(s); event(s, 'start'); } return; }
  if (s.phase === 'crash') { if (s.clock - s.phaseStarted >= RUN.crashMs) { if (s.lives === 0) setPhase(s, 'lost'); else respawn(s); } return; }
  if (s.phase === 'feedback') {
    if (s.clock < s.feedbackUntil) return;
    if (s.lives === 0) setPhase(s, 'lost');
    else if (s.segmentIndex === config.questions.length - 1) setPhase(s, 'finished');
    else { s.segmentIndex++; beginSegment(s); }
    return;
  }
  if (s.phase !== 'running' && s.phase !== 'arriving') return;
  s.runClock += dtMs;
  const station = stationAt(s.segmentIndex);
  if (s.phase === 'running') {
    const target = targetSpeed(s);
    s.speed = s.speed < target ? Math.min(target, s.speed + RUN.accel * dt) : Math.max(target, s.speed - RUN.accel * dt);
    if (station - s.distance <= RUN.brakeDistance) { s.arriveSpeed = Math.max(s.speed, 4); setPhase(s, 'arriving'); event(s, 'station'); }
  }
  if (s.phase === 'arriving') s.speed = Math.max(1.2, s.arriveSpeed * Math.sqrt(Math.max(0, station - s.distance) / RUN.brakeDistance));
  s.distance = Math.min(station, s.distance + s.speed * dt);
  s.maxDistance = Math.max(s.maxDistance, s.distance);
  const t = Math.min(1, (s.clock - s.laneStarted) / RUN.laneChangeMs);
  s.x = s.laneFrom + (laneX(s.lane) - s.laneFrom) * t * t * (3 - 2 * t);
  if (s.y > 0 || s.vy > 0) { s.vy -= GRAVITY * dt; s.y = Math.max(0, s.y + s.vy * dt); if (s.y === 0) s.vy = 0; }
  for (const segment of s.segments.slice(s.segmentIndex, s.segmentIndex + 2)) for (const o of segment.obstacles) {
    if (o.kind === 'moving-train' && s.movingActivated[o.id] === undefined && o.start - s.distance <= TRACK.movingTrainTrigger) s.movingActivated[o.id] = s.runClock;
  }
  collectCoins(s);
  if (s.clock >= s.shieldUntil) {
    const hit = hitTest(s);
    if (hit) {
      hit.cleared = true; s.crashed = hit; s.lives = Math.max(0, s.lives - 1); s.segmentEvents[s.segmentIndex].hits++;
      s.speed = 0; s.vy = 0; event(s, 'hit'); setPhase(s, 'crash');
      return;
    }
  }
  if (s.phase === 'arriving' && s.distance >= station - 1e-6) { s.speed = 0; s.y = 0; s.vy = 0; s.slideStarted = null; s.questionOpenedAt = s.clock; setPhase(s, 'question'); }
}

/** Sabit adımlı entegrasyon: her kare hızında aynı olaylar. */
export function advanceRun(s: RunState, config: StationRunConfig, milliseconds: number) {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0 || s.phase === 'ready' || s.phase === 'finished' || s.phase === 'lost') return;
  s.accumulator += milliseconds;
  while (s.accumulator >= RUN.stepMs - 1e-6) {
    s.accumulator = Math.max(0, s.accumulator - RUN.stepMs);
    step(s, config, RUN.stepMs);
    // `step` fazı değiştirir; TypeScript daraltmasını sıfırlamak için geniş türle okunur.
    const phase = s.phase as RunPhase;
    if (phase === 'question' || phase === 'finished' || phase === 'lost') { s.accumulator = 0; break; }
  }
}

/** İstasyondaki soruyu cevaplar: doğru +100; yanlışta puan ≥ 50 ise −50, değilse −1 can. */
export function answerQuestion(s: RunState, config: StationRunConfig, choice: number): boolean {
  const q = config.questions[s.segmentIndex];
  if (s.phase !== 'question' || !Number.isInteger(choice) || !q.choices[choice]) return false;
  const correctChoiceIndex = q.choices.findIndex((c) => c.correct);
  const correct = choice === correctChoiceIndex;
  const outcome = stationRunAnswer(s.score, correct);
  s.score += outcome.scoreDelta; s.lives -= outcome.livesLost;
  if (correct) s.correct++; else s.wrong++;
  s.results.push({ level: s.segmentIndex + 1, questionId: q.id, choiceCount: q.choices.length, selectedChoiceIndex: choice, correctChoiceIndex, correct,
    scoreDelta: outcome.scoreDelta, scoreAfter: s.score, livesLost: outcome.livesLost, livesAfter: s.lives,
    durationMs: Math.round(s.clock - s.questionOpenedAt),
    questionPrompt: q.prompt || 'Görsel soru', selectedAnswer: q.choices[choice].label || 'Görsel seçenek', correctAnswer: q.choices[correctChoiceIndex].label || 'Görsel seçenek',
    questionMediaUrl: q.mediaUrl ?? null, selectedMediaUrl: q.choices[choice].mediaUrl ?? null, correctMediaUrl: q.choices[correctChoiceIndex].mediaUrl ?? null });
  s.lastAnswer = { correct, choice, ...outcome };
  s.feedbackUntil = s.clock + (correct ? RUN.correctFeedbackMs : RUN.wrongFeedbackMs);
  if (correct) s.turboUntil = s.feedbackUntil + RUN.turboMs;
  event(s, correct ? 'correct' : 'wrong'); setPhase(s, 'feedback');
  return true;
}

export function runResult(s: RunState): FinishPlayAttemptInput {
  if (s.phase !== 'finished' && s.phase !== 'lost') throw new Error('Koşu henüz bitmedi.');
  return { score: s.score, lives: s.lives, correct: s.correct, wrong: s.wrong, attempts: s.results.length, completed: s.phase === 'finished',
    details: { templateKey: 'station-run', questions: s.results.map((r) => ({ ...r })), segments: s.segmentEvents.map((e) => ({ ...e })),
      distanceMeters: Math.round(s.maxDistance) } };
}
